import { IncomingMessage } from 'http';

import { NextApiRequest } from 'next';
import { NextRequest } from 'next/server';

import { User } from '../../resources/user/domain/types/User';
import { NullablePartial } from '../../shared/types/NullablePartial';
import { RouteName } from '../../shared/utilities/routing/routing';

import { browserLogger } from './browserLogger';

type CustomRequest = {
    url?: string;
    method?: string;
    query?: Partial<Record<string, string | string[]>>;
    body?: Record<string, unknown>;
    headers?: NullablePartial<Record<string, string>> | Request['headers'];
};

type InternalRouteName =
    | 'proxy'
    | 'global_error'
    | 'pages_global_error'
    | 'api_internal_redirections'
    | 'api_internal_redis_nav_destinations_menu'
    | 'api_internal_redis_nav_where_to_go_menu'
    | 'api_internal_redis_nav_hub_destination_menu'
    | 'api_internal_redis_page_home';

type LogContext = {
    [key: string]: unknown;
    locale?: string | null;
    routeName?: RouteName | InternalRouteName;
    request?: CustomRequest | Request | NextRequest | NextApiRequest | IncomingMessage;
    response?: {
        url?: string;
        status?: number;
        statusText?: string;
        body?: Record<string, unknown>;
    } | null;
    user?: NullablePartial<User> | null;
    currentPage?: number;
    previousPage?: number | null;
    pagination?: {
        firstPage?: number;
        lastPage?: number;
        currentPage?: number;
        nextPage?: number | null;
        previousPage?: number | null;
    };
    error?: unknown;
    location?: never; // Added by Datadog automatically in log.view.
};

type SerializedLogContext = LogContext;

export const logger = {
    error: logError,
    warning: logWarning,
    info: logInfo,
    debug: logDebug,
};

function logError(message: string, context: LogContext): void {
    const serializedContext = serializeContext(context);

    if (process.env.NODE_ENV === 'development') {
        console.error(message, { context: serializedContext });
    }

    if (isServer()) {
        return void import('./serverLogger').then(({ serverLogger }) => {
            serverLogger.error({ context: serializedContext }, message);
        });
    }

    const errorInstance = context.error instanceof Error ? context.error : undefined;
    return browserLogger.error(message, { context: serializedContext }, errorInstance);
}

function logWarning(message: string, context: LogContext) {
    const serializedContext = serializeContext(context);

    if (process.env.NODE_ENV === 'development') {
        console.warn(message, { context: serializedContext });
    }

    if (isServer()) {
        return void import('./serverLogger').then(({ serverLogger }) => {
            serverLogger.warn({ context: serializedContext }, message);
        });
    }

    return browserLogger.warn(message, { context: serializedContext });
}

function logInfo(message: string, context: LogContext) {
    const serializedContext = serializeContext(context);

    if (process.env.NODE_ENV === 'development') {
        console.info(message, { context: serializedContext });
    }

    if (isServer()) {
        return void import('./serverLogger').then(({ serverLogger }) => {
            serverLogger.info({ context: serializedContext }, message);
        });
    }

    return browserLogger.info(message, { context: serializedContext });
}

function logDebug(message: string, context: LogContext) {
    const serializedContext = serializeContext(context);

    if (process.env.NODE_ENV === 'development') {
        console.debug(message, { context: serializedContext });
    }

    if (isServer()) {
        return void import('./serverLogger').then(({ serverLogger }) => {
            serverLogger.debug({ context: serializedContext }, message);
        });
    }

    return browserLogger.debug(message, { context: serializedContext });
}

function isServer() {
    return typeof window === 'undefined';
}

export function serializeContext(context: LogContext): SerializedLogContext {
    const serializedContext: SerializedLogContext = { ...context };

    if (typeof context.user !== 'undefined') {
        serializedContext.user = serializeUser(context.user);
    }

    if (typeof context.error !== 'undefined') {
        serializedContext.error = serializeError(context.error);
    }

    if (typeof context.request !== 'undefined') {
        serializedContext.request = serializeRequest(context.request);
    }

    return serializedContext;
}

type SerializedUser = {
    accountId?: string | null;
    id?: number | null;
    isEvaneos?: boolean | null;
    isTcare?: boolean | null;
    status?: User['status'] | null;
    missingPhone?: boolean | null;
    missingLastName?: boolean | null;
};

function serializeUser(user: NullablePartial<User> | null): SerializedUser | null {
    if (user === null) {
        return null;
    }

    const serializedUser: SerializedUser = {
        accountId: user.accountId,
        id: user.id,
        isEvaneos: user.isEvaneos,
        isTcare: user.isTcare,
    };

    if (typeof user.phone !== 'undefined' && user.phone !== null) {
        serializedUser.missingPhone = !user.phone;
    }

    if (typeof user.lastName !== 'undefined' && user.lastName !== null) {
        serializedUser.missingLastName = !user.lastName;
    }

    if (typeof user.status !== 'undefined') {
        serializedUser.status = user.status;
    }

    return serializedUser;
}

type SerializedError = {
    [key: string]: unknown;
    message: string;
    name?: string;
    stack?: string;
};

function serializeError(error: unknown): SerializedError | undefined {
    if (error === null) {
        return;
    }

    if (error instanceof Error) {
        const { message, stack, name, ...rest } = error as Error & Record<string, unknown>;
        return { name, message, stack, ...rest };
    }

    if (typeof error === 'string') {
        return { message: error };
    }

    try {
        return { message: JSON.stringify(error) };
    } catch {
        return;
    }
}

function serializeRequest(request: NonNullable<LogContext['request']>) {
    const query = 'query' in request ? request.query : {};

    let serializedHeaders: NullablePartial<Record<string, string>> = {};
    if ('headers' in request) {
        try {
            // @ts-expect-error - Property 'headers' does not exist on type 'Request'.
            const headers = new Headers('headers' in request ? request.headers : {});

            [
                'accept',
                'accept-language',
                'cdn-loop',
                'cf-ray',
                'host',
                'iscachable',
                'referer',
                'sec-ch-ua-platform',
                'user-agent',
                'x-cloud-trace-context',
                'x-country-code',
                'x-currency',
                'x-datadog-parent-id',
                'x-datadog-tags',
                'x-datadog-trace-id',
                'x-forwarded-host',
                'x-forwarded-port',
                'x-forwarded-proto',
                'x-forwarded-scheme',
                'x-public-website-routing',
                'x-scheme',
                'x-ua-device',
                'x-varnish',
                'x-varnish-url',
            ].forEach((header) => {
                if (headers.has(header)) {
                    serializedHeaders[header] = headers.get(header);
                }
            });
        } catch {
            serializedHeaders = {};
        }
    }

    return {
        url: request.url,
        method: request.method,
        query,
        headers: serializedHeaders,
    };
}
